<template>
  <ol class="trip-days-list">
    <li
      v-for="(day, index) in days"
      :key="index"
      :class="['trip-day', { 'trip-day-open': openDay === index }]"
    >
      <button
        type="button"
        class="trip-day-row"
        :aria-expanded="openDay === index ? 'true' : 'false'"
        @click="toggle(index)"
      >
        <span class="trip-day-number">{{ index + 1 }}</span>
        <span class="trip-day-text">
          <span class="trip-day-label">{{ dayLabel(index) }}</span>
          <span class="trip-day-preview">{{ preview(day) }}</span>
        </span>
        <span class="trip-day-count">{{ t('ridePlacesCount', { count: places(day).length }) }}</span>
        <svg class="trip-day-chevron" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
          <path fill="currentColor" d="M7.4 8.6 12 13.2l4.6-4.6L18 10l-6 6-6-6z"/>
        </svg>
      </button>
      <div v-if="openDay === index" class="trip-day-body">
        <ul class="trip-day-places">
          <li v-for="(place, i) in places(day)" :key="i">{{ place }}</li>
        </ul>
        <button type="button" class="trip-day-go" @click="$emit('open-day', index)">
          {{ t('rideOpenDay') }}
        </button>
      </div>
    </li>
  </ol>
</template>

<script>
import { translationMixin } from "@/mixins/translationMixin";

/**
 * The trip summary's activities tab, in every design: one short row per day
 * (its places in one line and how many), opened in place to list them all. Emits 'open-day' with
 * the day index to go to that day's card.
 */
export default {
  name: 'RideDayList',
  mixins: [translationMixin],
  props: {
    // allRide.attractionsByDay
    days: { type: Array, default: () => [] },
    // index -> "Day 3 - 12/8", as the classic list labels days
    dayLabel: { type: Function, required: true },
  },
  data() {
    return { openDay: null };
  },
  methods: {
    places(day) {
      return ((day && day.daily_attractions) || [])
        .map(attraction => attraction && attraction.title && attraction.title.trim())
        .filter(Boolean);
    },
    preview(day) {
      return this.places(day).join(' · ');
    },
    toggle(index) {
      this.openDay = this.openDay === index ? null : index;
    },
  },
};
</script>

<style scoped>
.trip-days-list {
  list-style: none;
  margin: 0 auto;
  padding: 0;
  width: min(520px, 92vw);
}

.trip-day {
  border-bottom: 1px solid #ececef;
}

.trip-day-row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 4px;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  text-align: start;
  cursor: pointer;
}

.trip-day-number {
  flex: 0 0 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: var(--primary-background, #07A0C3);
  color: var(--primary-text-color, #fff);
  font-size: 15px;
  font-weight: 700;
}

.trip-day-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.trip-day-label {
  font-size: 16px;
  font-weight: 700;
  color: #1d1d1f;
}

.trip-day-preview {
  overflow: hidden;
  font-size: 14px;
  color: #6e6e73;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.trip-day-count {
  flex: 0 0 auto;
  font-size: 13px;
  font-weight: 600;
  color: var(--primary-color, #07A0C3);
}

.trip-day-chevron {
  flex: 0 0 20px;
  color: #8e8e93;
  transition: transform 0.2s ease;
}

.trip-day-open .trip-day-chevron {
  transform: rotate(180deg);
}

.trip-day-body {
  padding: 0 4px 14px;
}

.trip-day-places {
  margin: 0;
  padding: 0;
  list-style: none;
}

.trip-day-places li {
  position: relative;
  padding: 4px 0;
  padding-inline-start: 46px;
  font-size: 15px;
  line-height: 1.4;
  color: #3a3a3c;
}

.trip-day-places li::before {
  content: "";
  position: absolute;
  top: 12px;
  inset-inline-start: 30px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--primary-color, #07A0C3);
}

.trip-day-go {
  margin-top: 6px;
  margin-inline-start: 46px;
  padding: 6px 14px;
  border: 0;
  border-radius: 999px;
  background: none;
  box-shadow: inset 0 0 0 1.5px var(--primary-color, #07A0C3);
  color: var(--primary-color, #07A0C3);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}
</style>
